// src/routerProvider.ts
import { useRouter as useRouter2 } from "next/router";
import qs from "qs";
import { handleUseParams } from "@pankod/refine-core";

// src/prompt.tsx
import { useRouter } from "next/router";
import { useEffect } from "react";
var Prompt = ({
  message,
  when,
  setWarnWhen
}) => {
  const router = useRouter();
  useEffect(() => {
    const routeChangeStart = () => {
      if (when) {
        const allowTransition = window.confirm(message);
        if (allowTransition) {
          setWarnWhen == null ? void 0 : setWarnWhen(false);
        } else {
          router.events.emit("routeChangeError");
          throw "Abort route change due to unsaved changes prompt. Ignore this error.";
        }
      }
    };
    router.events.on("routeChangeStart", routeChangeStart);
    return () => router.events.off("routeChangeStart", routeChangeStart);
  }, [when]);
  return null;
};

// src/refineLink.tsx
import React from "react";
import Link from "next/link";
var RefineLink = ({
  children,
  ...props
}) => /* @__PURE__ */ React.createElement(Link, {
  href: "to" in props ? props.to : props.href,
  legacyBehavior: false,
  ...props
}, children);

// src/routerProvider.ts
var RouterProvider = {
  useHistory: () => {
    const router = useRouter2();
    const { push, replace, back } = router;
    return {
      push,
      replace,
      goBack: back
    };
  },
  useLocation: () => {
    const router = useRouter2();
    const { query, asPath } = router;
    const queryParams = qs.stringify(query);
    const sliceLength = Math.min(
      ...[
        asPath.indexOf("?") > 0 ? asPath.indexOf("?") : asPath.length,
        asPath.indexOf("#") > 0 ? asPath.indexOf("#") : asPath.length
      ]
    );
    return {
      pathname: asPath.slice(0, sliceLength),
      search: queryParams && `?${queryParams}`
    };
  },
  useParams: () => {
    const router = useRouter2();
    const { query } = router;
    return handleUseParams(query);
  },
  Prompt,
  Link: RefineLink
};

// src/nextRouteComponent.tsx
import React2 from "react";
import {
  useRefineContext,
  LayoutWrapper,
  ErrorComponent,
  useResource,
  LoginPage as DefaultLoginPage,
  CanAccess
} from "@pankod/refine-core";
var { useHistory, useLocation, useParams } = RouterProvider;
var NextRouteComponent = ({
  initialData,
  children,
  ...rest
}) => {
  const { resources } = useResource();
  const { push } = useHistory();
  const {
    resource: routeResourceName,
    action,
    id
  } = useParams();
  const { pathname } = useLocation();
  const { DashboardPage, catchAll, LoginPage } = useRefineContext();
  const resource = resources.find(
    (res) => res.name === routeResourceName || res.route === routeResourceName
  );
  const isServer = typeof window !== "undefined";
  const renderLoginRouteElement = () => {
    if (LoginPage)
      return /* @__PURE__ */ React2.createElement(LoginPage, null);
    return /* @__PURE__ */ React2.createElement(DefaultLoginPage, null);
  };
  if (routeResourceName === "login") {
    return renderLoginRouteElement();
  }
  if (pathname === "/") {
    if (DashboardPage) {
      return /* @__PURE__ */ React2.createElement(LayoutWrapper, null, /* @__PURE__ */ React2.createElement(CanAccess, {
        resource: "dashboard",
        action: "list",
        fallback: catchAll != null ? catchAll : /* @__PURE__ */ React2.createElement(ErrorComponent, null),
        params: {
          resource
        }
      }, /* @__PURE__ */ React2.createElement(DashboardPage, {
        initialData,
        ...rest
      })));
    } else {
      if (isServer)
        push(`/${resources[0].route}`);
      return null;
    }
  }
  if (resource) {
    const {
      list,
      create,
      edit,
      show,
      name,
      canCreate,
      canEdit,
      canShow,
      canDelete,
      options
    } = resource;
    const List = list != null ? list : () => null;
    const Create = create != null ? create : () => null;
    const Edit = edit != null ? edit : () => null;
    const Show = show != null ? show : () => null;
    const renderCrud = () => {
      switch (action) {
        case void 0: {
          return /* @__PURE__ */ React2.createElement(CanAccess, {
            resource: name,
            action: "list",
            fallback: catchAll != null ? catchAll : /* @__PURE__ */ React2.createElement(ErrorComponent, null),
            params: {
              resource
            }
          }, /* @__PURE__ */ React2.createElement(List, {
            name,
            canCreate,
            canEdit,
            canDelete,
            canShow,
            initialData,
            options,
            ...rest
          }));
        }
        case "create":
        case "clone": {
          return /* @__PURE__ */ React2.createElement(CanAccess, {
            resource: name,
            action: "create",
            fallback: catchAll != null ? catchAll : /* @__PURE__ */ React2.createElement(ErrorComponent, null),
            params: {
              resource
            }
          }, /* @__PURE__ */ React2.createElement(Create, {
            name,
            canCreate,
            canEdit,
            canDelete,
            canShow,
            initialData,
            options,
            ...rest
          }));
        }
        case "edit": {
          return /* @__PURE__ */ React2.createElement(CanAccess, {
            resource: name,
            action: "edit",
            params: { id, resource },
            fallback: catchAll != null ? catchAll : /* @__PURE__ */ React2.createElement(ErrorComponent, null)
          }, /* @__PURE__ */ React2.createElement(Edit, {
            name,
            canCreate,
            canEdit,
            canDelete,
            canShow,
            initialData,
            options,
            ...rest
          }));
        }
        case "show": {
          return /* @__PURE__ */ React2.createElement(CanAccess, {
            resource: name,
            action: "show",
            params: { id, resource },
            fallback: catchAll != null ? catchAll : /* @__PURE__ */ React2.createElement(ErrorComponent, null)
          }, /* @__PURE__ */ React2.createElement(Show, {
            name,
            canCreate,
            canEdit,
            canDelete,
            canShow,
            initialData,
            options,
            ...rest
          }));
        }
      }
    };
    return /* @__PURE__ */ React2.createElement(LayoutWrapper, null, renderCrud());
  }
  return catchAll ? /* @__PURE__ */ React2.createElement(React2.Fragment, null, catchAll) : /* @__PURE__ */ React2.createElement(LayoutWrapper, null, /* @__PURE__ */ React2.createElement(ErrorComponent, null));
};

// src/checkAuthentication.ts
var checkAuthentication = async (authProvider, context) => {
  var _a;
  let isAuthenticated = false;
  if (context.resolvedUrl.includes("/login")) {
    return {
      props: {},
      isAuthenticated
    };
  }
  try {
    await ((_a = authProvider.checkAuth) == null ? void 0 : _a.call(authProvider, context));
    isAuthenticated = true;
  } catch (error) {
    const encodeURI = () => {
      if (context.resolvedUrl === "/" || context.resolvedUrl === void 0) {
        return "/login";
      }
      return `/login?to=${encodeURIComponent(context.resolvedUrl)}`;
    };
    return {
      isAuthenticated,
      redirect: {
        destination: encodeURI(),
        permanent: false
      }
    };
  }
  return { props: {}, isAuthenticated };
};
export {
  RefineLink as Link,
  NextRouteComponent,
  checkAuthentication,
  RouterProvider as default
};
//# sourceMappingURL=index.js.map